import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from "./ui/table";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";

const DataTable = ({
  data = [],
  columns = [],
  caption,
  itemsPerPage = 5,
  getLevelIcon,
  getRowBackgroundColor,
  isAddButtonEnabled = false,
  addButtonLabel = "Yeni Ekle",
  onAddClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // Search term for filtering

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    return data.filter((item) =>
      columns.some((col) =>
        String(item[col.accessor] ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, data, columns]);

  // Paginate the data based on the current page
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [currentPage, filteredData, itemsPerPage]);

  // Pagination için gösterilecek sayfa numaralarını hesapla
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <div className="flex items-center justify-between w-full">
        <div className="w-full max-w-md">
          <Input
            placeholder="Filtrele..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {isAddButtonEnabled && onAddClick && (
          <Button variant="default" onClick={onAddClick}>
            {addButtonLabel || "Yeni Ekle"}
          </Button>
        )}
      </div>

      <Table className="w-full">
        {caption && <TableCaption>{caption}</TableCaption>}

        <TableHeader>
          <TableRow>
            {columns.map((col, index) => (
              <TableHead
                key={`head-${col.accessor || index}`}
                className="text-center"
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row) => (
              <TableRow
                key={`row-${row.id}`}
                className={
                  getRowBackgroundColor ? getRowBackgroundColor(row.status) : ""
                } // Apply background color from the passed function
              >
                {columns.map((col) => {
                  const value = row[col.accessor];
                  return (
                    <TableCell
                      key={`cell-${row.id}-${col.accessor}`}
                      className="text-center"
                    >
                      {col.cell ? (
                        col.cell(row)
                      ) : col.accessor === "level" ? (
                        <>
                          {getLevelIcon ? getLevelIcon(value) : null} {value}{" "}
                          {/* Display level icon */}
                        </>
                      ) : Array.isArray(value) ? (
                        value.join(", ") // Display nodes as comma-separated values
                      ) : (
                        value // Default rendering for other columns
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                Kayıt bulunamadı.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            />
          </PaginationItem>
          {getPageNumbers().map((page, i) => (
            <PaginationItem key={`page-${page}-${i}`}>
              {page === "..." ? (
                <span className="px-2">...</span>
              ) : (
                <PaginationLink
                  href="#"
                  isActive={currentPage === page}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}
          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default DataTable;
